import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileImage, Loader2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { getBlockDrawingTypes, uploadBlockDrawings } from '../../services/api/implementation/impl';
import { normalizeDrawingTypes } from '../../utils/accommodationDrawingUtils';

const MAX_FILES = 10;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const BlockDrawingUploadModal = ({ blockId, blockName, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [drawingType, setDrawingType] = useState('');
  const [fileEntries, setFileEntries] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: drawingTypesRaw,
    isLoading: isLoadingTypes,
    isError: isTypesError
  } = useQuery({
    queryKey: ['blockDrawingTypes'],
    queryFn: () => getBlockDrawingTypes()
  });

  const drawingTypes = useMemo(() => normalizeDrawingTypes(drawingTypesRaw), [drawingTypesRaw]);

  const addFiles = (incomingFiles) => {
    if (!incomingFiles?.length) return;

    const next = [...fileEntries];
    for (const file of incomingFiles) {
      if (next.length >= MAX_FILES) {
        toast.error(`You can upload up to ${MAX_FILES} files at a time`);
        break;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50 MB limit`);
        continue;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        name: file.name
      });
    }
    setFileEntries(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id) => {
    setFileEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateFileName = (id, name) => {
    setFileEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, name } : entry)));
  };

  const handleSubmit = async () => {
    if (!drawingType) {
      toast.error('Please select a drawing type');
      return;
    }
    if (!fileEntries.length) {
      toast.error('Please upload at least one file');
      return;
    }

    const formData = new FormData();
    formData.append('drawing_type', drawingType);
    fileEntries.forEach((entry) => {
      formData.append('files', entry.file);
    });
    fileEntries.forEach((entry) => {
      formData.append('names', (entry.name || entry.file.name).trim());
    });

    setIsSubmitting(true);
    try {
      await uploadBlockDrawings(blockId, formData);
      toast.success('Block drawings uploaded successfully');
      await queryClient.invalidateQueries({ queryKey: ['blockDrawings', blockId] });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to upload block drawings'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDirty = drawingType !== '' || fileEntries.length > 0;

  return (
    <Modal
      width={52}
      confirmOnClose={true}
      isDirty={isDirty}
      name={`Upload block drawings${blockName ? ` — ${blockName}` : ''}`}
      onClose={onClose}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-gray-600">
          Select a drawing type and upload up to {MAX_FILES} files (max 50 MB each).
        </p>

        <div>
          <label htmlFor="block-drawing-type" className="mb-2 block text-sm font-medium text-gray-700">
            Drawing type <span className="text-red-500">*</span>
          </label>
          {isLoadingTypes ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading drawing types...
            </div>
          ) : isTypesError ? (
            <p className="text-sm text-red-600">Failed to load drawing types. Please try again.</p>
          ) : (
            <select
              id="block-drawing-type"
              value={drawingType}
              onChange={(e) => setDrawingType(e.target.value)}
              disabled={isSubmitting || !drawingTypes.length}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-[#2B5C66] focus:outline-none focus:ring-2 focus:ring-[#2B5C66]/20 disabled:bg-gray-50">
              <option value="">Select drawing type</option>
              {drawingTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <span className="mb-2 block text-sm font-medium text-gray-700">
            Files <span className="text-red-500">*</span>
          </span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            disabled={isSubmitting || fileEntries.length >= MAX_FILES}
            onChange={(e) => addFiles(Array.from(e.target.files || []))}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting || fileEntries.length >= MAX_FILES}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!isSubmitting) addFiles(Array.from(e.dataTransfer.files || []));
            }}
            className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/60 px-4 py-8 text-center transition hover:border-[#2B5C66] hover:bg-[#F0F7F8] disabled:cursor-not-allowed disabled:opacity-60">
            <Upload className="mb-2 h-8 w-8 text-[#2B5C66]" />
            <p className="text-sm font-medium text-gray-800">Click or drag files here</p>
            <p className="mt-1 text-xs text-gray-500">
              {fileEntries.length}/{MAX_FILES} files selected
            </p>
          </button>
        </div>

        {fileEntries.length > 0 ? (
          <div className="flex flex-col gap-3">
            {fileEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <FileImage className="mt-1 h-5 w-5 shrink-0 text-[#2B5C66]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-800" title={entry.file.name}>
                    {entry.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(entry.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <input
                    type="text"
                    value={entry.name}
                    onChange={(e) => updateFileName(entry.id, e.target.value)}
                    placeholder="Display name (optional)"
                    disabled={isSubmitting}
                    className="mt-2 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:border-[#2B5C66] focus:outline-none focus:ring-1 focus:ring-[#2B5C66]/20"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(entry.id)}
                  disabled={isSubmitting}
                  className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  aria-label="Remove file">
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button type="button" onClick={onClose} displayType="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isSubmitting || !drawingType || !fileEntries.length || isLoadingTypes || isTypesError
            }>
            {isSubmitting ? 'Uploading...' : 'Upload drawings'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BlockDrawingUploadModal;
